import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {
  AuthBaseGetApi,
  LoginGetApi,
  MiddleWareGetApi,
  AuthBasePostApi,
  LoginPostApi,
  MiddleWarePostApi,
  AuthBaseNoauthPostApi,
  ZendeskPostApi,
} from '../services/apiService';

// GET fetchers (used when no body is passed)
const fetcherMap = {
  auth: (url) => AuthBaseGetApi(url),
  login: (url) => LoginGetApi(url),
  middleware: (url) => MiddleWareGetApi(url),
};

// POST-as-query fetchers (used when a body is passed — still cached like a GET)
const bodyFetcherMap = {
  auth: (url, body) => AuthBasePostApi(url, body),
  login: (url, body) => LoginPostApi(url, body),
  middleware: (url, body) => MiddleWarePostApi(url, body),
};

// POST mutation fetchers (for useAppMutation — unrelated to caching)
const mutatorMap = {
  auth: (url, data) => AuthBasePostApi(url, data),
  authNoAuth: (url, data) => AuthBaseNoauthPostApi(url, data),
  login: (url, data) => LoginPostApi(url, data),
  middleware: (url, data) => MiddleWarePostApi(url, data),
  zendesk: (url, data) => ZendeskPostApi(url, data),
};

/**
 * Universal hook for SWR GET-style requests.
 * Pass `body` (4th arg) if the underlying endpoint is a POST-as-query call
 * (e.g. fetch lists via POST) — it still caches like a GET, just with the
 * body baked into the cache key.
 */
export const useAppQuery = (apiType, url, options = {}, body) => {
  const hasBody = body !== undefined;
  const fetcher = hasBody ? bodyFetcherMap[apiType] : fetcherMap[apiType];

  if (!fetcher) {
    throw new Error(`Unsupported API Type provided to useAppQuery: ${apiType}`);
  }

  const key = url
    ? hasBody
      ? [apiType, url, JSON.stringify(body)]
      : [apiType, url]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    key,
    hasBody
      ? ([, targetUrl, bodyStr]) => fetcher(targetUrl, JSON.parse(bodyStr))
      : ([, targetUrl]) => fetcher(targetUrl),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      ...options,
    }
  );

  return {
    data,
    error,
    isLoading,          // true ONLY when there's no cached data yet
    isRefreshing: isValidating && !isLoading, // true during silent background refetch
    mutate,
  };
};

/**
 * Universal hook for SWR POST mutation requests (fire on trigger()).
 */
export const useAppMutation = (apiType, url, options = {}) => {
  const mutator = mutatorMap[apiType];

  if (!mutator) {
    throw new Error(`Unsupported API Type provided to useAppMutation: ${apiType}`);
  }

  const { data, error, isMutating, trigger, reset } = useSWRMutation(
    url ? [apiType, url] : null,
    ([, targetUrl], { arg }) => mutator(targetUrl, arg),
    {
      throwOnError: false,
      ...options,
    }
  );

  return { data, error, isMutating, trigger, reset };
};